// Objektliste und Portfolio-Kennzahlen (README §5.1) – reine Funktionen.

import { istWohnend } from '../dashboard/leerstand';
import { aktuellerStand, leseStand } from '../mieter/mietstaende';
import { objektAdresse, objektName, type ObjektKopfRoh } from './objektKopf';
import { verwaltungsartVonObjekt, type VerwaltungsartKurz } from './sidebarNav';
import { sichereUrl } from './url';
import { istArchiviert } from './archiv';
import { ohneZimmer, wohnungJeZimmer, zugerechneteEinheit } from './zimmer';

export interface ObjektListeRoh extends ObjektKopfRoh {
  wohneinheiten?: number | string | null;
  wohnflaeche?: number | string | null;
  zustaendig?: string | null;
  zustaendig_id?: string | null;
  zustaendig_typ?: string | null;
  eigentuemer?: string | null;
  status?: string | null;
}

export interface EinheitListeRoh { id: string; objekt_id: string; wohnflaeche: number | string | null; uebergeordnete_einheit_id?: string | null }

export interface MieterListeRoh {
  objekt_id: string | null;
  einheit_id: string | null;
  status: string | null;
  kaltmiete: number | string | null;
  miet_historie: unknown;
  mietbeginn: string | null;
  mietende: string | null;
}

export interface ObjektZeile {
  id: string;
  nummer: string;
  name: string;
  adresse: string;
  bildUrl: string | null;
  verwaltungsart: VerwaltungsartKurz | null;
  zustaendig: string;
  zustaendigId: string | null;
  einheiten: number;
  belegt: number;
  /** #596: Einheiten, die für Belegung/Leerstand zählen (MV, SEV, GV; bei WEG nur SEV-Einheiten) */
  mietEinheiten: number;
  /** davon mit wohnendem Mieter */
  mietBelegt: number;
  /** 0–100, ganzzahlig, aus mietEinheiten/mietBelegt */
  belegungProzent: number;
  flaeche: number;
  /** Suchtext (normalisiert in filtereObjekte) */
  suchtext: string;
  archiviert: boolean;
  roh: ObjektListeRoh;
}

const zahl = (v: unknown): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) ? n : 0;
};

export function zustaendigName(o: Pick<ObjektListeRoh, 'zustaendig'>): string {
  return (o.zustaendig ?? '').trim() || 'Global';
}

/**
 * #596: Zählt die Einheit für Belegung und Leerstand? Leerstand gibt es nur, wo wir vermieten:
 * Mietverwaltung, SEV und GV ganz, bei WEG nur Einheiten mit laufendem SEV-Vertrag.
 * Eigentumswohnungen ohne Mietvertrag bei uns sind kein Leerstand.
 */
export function zaehltFuerLeerstand(va: VerwaltungsartKurz | null, einheitId: string, sevEinheiten: ReadonlySet<string>): boolean {
  return va === 'WEG' ? sevEinheiten.has(einheitId) : true;
}

/** Einheiten mit laufendem SEV-Vertrag (einheit_eigentuemer.sev_status gesetzt, vertrag_bis offen oder ab heute). */
export function sevEinheitenAus(zeilen: readonly { einheit_id: string; sev_status: string | null; vertrag_bis: string | null }[], heute: string): Set<string> {
  return new Set(zeilen
    .filter(z => (z.sev_status ?? '').trim() !== '' && (!z.vertrag_bis || z.vertrag_bis.slice(0, 10) >= heute))
    .map(z => z.einheit_id));
}

/**
 * Eine Zeile je Objekt. Einheiten und Belegung aus objekt_einheiten + wohnenden Mietern;
 * hat ein Objekt (noch) keine Einheiten-Zeilen, gilt das Feld wohneinheiten und die Zahl
 * der wohnenden Mieter (höchstens so viele wie Einheiten).
 * Fläche: objekte.wohnflaeche, sonst Summe der Einheiten.
 * Zimmer (#499) zählen nicht einzeln: ein vermietetes Zimmer belegt seine Wohnung.
 */
export function baueObjektZeilen(
  objekte: readonly ObjektListeRoh[],
  einheiten: readonly EinheitListeRoh[],
  mieter: readonly MieterListeRoh[],
  sevEinheiten: ReadonlySet<string> = new Set(),
): ObjektZeile[] {
  const karte = wohnungJeZimmer(einheiten);
  const einheitenJe = new Map<string, EinheitListeRoh[]>();
  for (const e of ohneZimmer(einheiten)) {
    const l = einheitenJe.get(e.objekt_id) ?? [];
    l.push(e);
    einheitenJe.set(e.objekt_id, l);
  }
  const belegteEinheiten = new Set<string>();
  const wohnendeJeObjekt = new Map<string, number>();
  for (const m of mieter) {
    if (!istWohnend(m.status)) continue;
    const ziel = zugerechneteEinheit(m.einheit_id, karte);
    if (ziel) belegteEinheiten.add(ziel);
    if (m.objekt_id) wohnendeJeObjekt.set(m.objekt_id, (wohnendeJeObjekt.get(m.objekt_id) ?? 0) + 1);
  }

  return objekte.map(o => {
    const eig = einheitenJe.get(o.id) ?? [];
    const va = verwaltungsartVonObjekt(o);
    let anzahl: number;
    let belegt: number;
    let mietEinheiten: number;
    let mietBelegt: number;
    if (eig.length > 0) {
      anzahl = eig.length;
      belegt = eig.filter(e => belegteEinheiten.has(e.id)).length;
      const miet = eig.filter(e => zaehltFuerLeerstand(va, e.id, sevEinheiten));
      mietEinheiten = miet.length;
      mietBelegt = miet.filter(e => belegteEinheiten.has(e.id)).length;
    } else {
      anzahl = Math.max(0, Math.round(zahl(o.wohneinheiten)));
      belegt = Math.min(anzahl, wohnendeJeObjekt.get(o.id) ?? 0);
      // ohne Einheiten-Zeilen lässt sich bei WEG keine SEV-Einheit erkennen
      mietEinheiten = va === 'WEG' ? 0 : anzahl;
      mietBelegt = va === 'WEG' ? 0 : belegt;
    }
    const flaecheObjekt = zahl(o.wohnflaeche);
    const flaeche = flaecheObjekt > 0 ? flaecheObjekt : eig.reduce((s, e) => s + zahl(e.wohnflaeche), 0);
    const name = objektName(o);
    const adresse = objektAdresse(o);
    const nummer = (o.objektnummer ?? '').trim();
    return {
      id: o.id,
      nummer,
      name,
      adresse,
      bildUrl: sichereUrl(o.bild_url),
      verwaltungsart: va,
      zustaendig: zustaendigName(o),
      zustaendigId: o.zustaendig_id ?? null,
      einheiten: anzahl,
      belegt,
      mietEinheiten,
      mietBelegt,
      belegungProzent: mietEinheiten > 0 ? Math.round((mietBelegt / mietEinheiten) * 100) : 0,
      flaeche: Math.round(flaeche * 100) / 100,
      suchtext: [name, adresse, nummer, o.eigentuemer ?? ''].join(' '),
      archiviert: istArchiviert(o.status),
      roh: o,
    };
  });
}

const nummernSortierung = new Intl.Collator('de', { numeric: true });

/** Nach Objektnummer (numerisch, 0004.1 nach 0004); ohne Nummer ans Ende. */
export function sortiereNachNummer(zeilen: readonly ObjektZeile[]): ObjektZeile[] {
  return [...zeilen].sort((a, b) => {
    if (!a.nummer && !b.nummer) return a.name.localeCompare(b.name, 'de');
    if (!a.nummer) return 1;
    if (!b.nummer) return -1;
    return nummernSortierung.compare(a.nummer, b.nummer);
  });
}

export type VaFilter = 'alle' | 'MV' | 'WEG' | 'SEV';

const normal = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');

/** Filter der Objektliste. Archivierte erscheinen nur mit „archiv: true“ (dann nur sie). */
export function filtereObjekte(zeilen: readonly ObjektZeile[], f: { suche: string; va: VaFilter; zustaendig: string | null; archiv?: boolean }): ObjektZeile[] {
  const q = normal(f.suche.trim());
  return zeilen.filter(z =>
    z.archiviert === (f.archiv ?? false)
    && (f.va === 'alle' || z.verwaltungsart === f.va)
    && (!f.zustaendig || z.zustaendig === f.zustaendig)
    && (!q || normal(z.suchtext).includes(q)));
}

// ── Portfolio ────────────────────────────────────────────────────────

export interface Portfolio {
  objekte: number;
  einheiten: number;
  /** #596: Einheiten, die für Belegung/Leerstand zählen; belegt, frei und belegungProzent beziehen sich darauf */
  mietEinheiten: number;
  belegt: number;
  frei: number;
  flaeche: number;
  /** 0–100 */
  belegungProzent: number;
}

export function portfolio(zeilen: readonly ObjektZeile[]): Portfolio {
  const einheiten = zeilen.reduce((s, z) => s + z.einheiten, 0);
  const mietEinheiten = zeilen.reduce((s, z) => s + z.mietEinheiten, 0);
  const belegt = zeilen.reduce((s, z) => s + z.mietBelegt, 0);
  return {
    objekte: zeilen.length,
    einheiten,
    mietEinheiten,
    belegt,
    frei: mietEinheiten - belegt,
    flaeche: Math.round(zeilen.reduce((s, z) => s + z.flaeche, 0) * 100) / 100,
    belegungProzent: mietEinheiten > 0 ? Math.round((belegt / mietEinheiten) * 100) : 0,
  };
}

/** Objekte mit freien Einheiten, die meisten zuerst. */
export function leerstandListe(zeilen: readonly ObjektZeile[], max: number): { id: string; name: string; frei: number }[] {
  return zeilen
    .map(z => ({ id: z.id, name: z.name, frei: z.mietEinheiten - z.mietBelegt }))
    .filter(x => x.frei > 0)
    .sort((a, b) => b.frei - a.frei || a.name.localeCompare(b.name, 'de'))
    .slice(0, max);
}

/** Anzahl Objekte je Zuständigem, die meisten zuerst. */
export function zustaendigkeiten(zeilen: readonly ObjektZeile[], max: number): { name: string; anzahl: number }[] {
  const m = new Map<string, number>();
  for (const z of zeilen) m.set(z.zustaendig, (m.get(z.zustaendig) ?? 0) + 1);
  return [...m.entries()]
    .map(([name, anzahl]) => ({ name, anzahl }))
    .sort((a, b) => b.anzahl - a.anzahl || a.name.localeCompare(b.name, 'de'))
    .slice(0, max);
}

/** Erster Tag der letzten `n` Monate bis einschließlich des Monats von `heute` (ISO, aufsteigend). */
export function letzteMonate(heute: string, n: number): string[] {
  const [j, m] = heute.split('-').map(Number);
  const aus: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1 - i, 1));
    aus.push(d.toISOString().slice(0, 10));
  }
  return aus;
}

function monatsende(monatsanfang: string): string {
  const [j, m] = monatsanfang.split('-').map(Number);
  return new Date(Date.UTC(j ?? 1970, m ?? 1, 0)).toISOString().slice(0, 10);
}

/** Kaltmiete eines Mieters, gültig am Stichtag (Stand aus miet_historie, sonst Spiegelfeld). */
export function kaltmieteAm(m: Pick<MieterListeRoh, 'kaltmiete' | 'miet_historie'>, stichtag: string): number {
  const historie = Array.isArray(m.miet_historie) ? m.miet_historie.map((r, i) => leseStand(r, i)) : [];
  const stand = aktuellerStand(historie, stichtag);
  return stand ? stand.kaltmiete : zahl(m.kaltmiete);
}

/**
 * Soll-Kaltmiete je Monat über alle Mietverhältnisse.
 * Ein Mieter zählt in einem Monat, wenn das Mietverhältnis den Monat berührt
 * (mietbeginn ≤ Monatsende und mietende leer oder ≥ Monatsanfang).
 * Ohne Mietbeginn zählt nur, wer heute wohnt.
 */
export function sollKaltmieteJeMonat(mieter: readonly MieterListeRoh[], monate: readonly string[]): number[] {
  return monate.map(anfang => {
    const ende = monatsende(anfang);
    let summe = 0;
    for (const m of mieter) {
      const beginn = m.mietbeginn?.slice(0, 10) || null;
      const schluss = m.mietende?.slice(0, 10) || null;
      const laeuft = beginn
        ? beginn <= ende && (!schluss || schluss >= anfang)
        : istWohnend(m.status) && (!schluss || schluss >= anfang);
      if (laeuft) summe += kaltmieteAm(m, beginn && beginn > anfang ? beginn : anfang);
    }
    return Math.round(summe * 100) / 100;
  });
}
